import React from 'react';
import {
  DOCUMENT_CATEGORY_OPTIONS,
  REMINDER_TYPE_OPTIONS,
  type ReminderDueState,
  type ReminderType,
  type RepairStatus,
  type VehicleStatus,
} from '../types';
import { reminderStateLabel, vehicleStatusLabel } from '../format';

type Tone = { dot: string; text: string };

const VEHICLE_TONE: Record<VehicleStatus, Tone> = {
  active: { dot: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-300' },
  workshop: { dot: 'bg-violet-500', text: 'text-violet-700 dark:text-violet-300' },
  inactive: { dot: 'bg-slate-400', text: 'text-app-text-muted' },
};

const REMINDER_TONE: Record<ReminderDueState, Tone> = {
  ok: { dot: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-300' },
  soon: { dot: 'bg-amber-500', text: 'text-amber-800 dark:text-amber-300' },
  overdue: { dot: 'bg-rose-500', text: 'text-rose-700 dark:text-rose-300' },
  done: { dot: 'bg-slate-400', text: 'text-app-text-muted' },
};

const REPAIR_TONE: Record<RepairStatus, Tone & { label: string }> = {
  open: { dot: 'bg-slate-400', text: 'text-app-text-muted', label: 'Offen' },
  in_progress: { dot: 'bg-violet-500', text: 'text-violet-700 dark:text-violet-300', label: 'In Bearbeitung' },
  done: { dot: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-300', label: 'Abgeschlossen' },
};

/** Status als Punkt und Text, ohne Fläche: ruhiger in Tabellen und Kopfzeilen. */
function Pill({ label, tone, className = '' }: { label: string; tone: Tone; className?: string }) {
  return (
    <span className={`inline-flex max-w-full shrink-0 items-center gap-1.5 whitespace-nowrap text-xs font-medium ${tone.text} ${className}`}>
      <span className={`h-2 w-2 shrink-0 rounded-full ${tone.dot}`} aria-hidden />
      <span className="truncate">{label}</span>
    </span>
  );
}

export function VehicleStatusBadge({
  status,
  hasDue,
  className,
}: {
  status: VehicleStatus;
  hasDue?: boolean;
  className?: string;
}) {
  if (status === 'active' && hasDue) {
    return <Pill label="Wartung fällig" tone={REMINDER_TONE.soon} className={className} />;
  }
  return <Pill label={vehicleStatusLabel(status)} tone={VEHICLE_TONE[status]} className={className} />;
}

export function ReminderStateBadge({ state, className }: { state: ReminderDueState; className?: string }) {
  return <Pill label={reminderStateLabel(state)} tone={REMINDER_TONE[state]} className={className} />;
}

export function RepairStatusBadge({ status, className }: { status: RepairStatus; className?: string }) {
  const tone = REPAIR_TONE[status];
  return <Pill label={tone.label} tone={tone} className={className} />;
}

export function DocumentCategoryBadge({ category }: { category: string }) {
  const label = DOCUMENT_CATEGORY_OPTIONS.find((o) => o.value === category)?.label ?? category;
  const dot =
    category === 'contract'
      ? 'bg-blue-500'
      : category === 'insurance'
        ? 'bg-violet-500'
        : category === 'invoice'
          ? 'bg-emerald-500'
          : category === 'repair'
            ? 'bg-amber-500'
            : 'bg-slate-400';
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5 text-xs" title={label}>
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} aria-hidden />
      <span className="truncate text-app-text-muted">{label}</span>
    </span>
  );
}

export function ReminderTypeLabel({ type }: { type: ReminderType }) {
  const label = REMINDER_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;
  const dot =
    type === 'oil_change'
      ? 'bg-amber-500'
      : type === 'inspection'
        ? 'bg-blue-500'
        : type === 'hu_au'
          ? 'bg-rose-500'
          : type === 'tire_change'
            ? 'bg-slate-500'
            : type === 'insurance'
              ? 'bg-violet-500'
              : type === 'lease_end'
                ? 'bg-indigo-500'
                : 'bg-slate-400';
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5 text-xs" title={label}>
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} aria-hidden />
      <span className="truncate text-app-text-muted">{label}</span>
    </span>
  );
}
